import { ApiResponse } from '@/lib/api/api-response';
import { apiClient } from '@/lib/api/base-client';
import { EventModel } from '../../types/EventModel';
import {
  EventCountOverTimeByTypeDTO,
  EventCountOverTimeDTO,
  EventCreateDTO,
  EventFilterOptionsDTO,
  EventGroupDto,
  EventGroupListDto,
  EventReadDTO,
  EventUpdateDTO,
} from './event.contracts';
import { GroupPerTimeType } from '@/modules/dashboards/components/widgets/events-over-time-widget/group-per-time-type.enum';
import { PagePaginationQueryDtoParams, PagePaginationResultDto } from '@/lib/api/pagination.page.dto';

export class EventService {
  public basePath = 'event';

  async create(eventToCreate: EventCreateDTO): Promise<ApiResponse<undefined>> {
    try {
      const applicationResponse = await apiClient.post<undefined>(this.basePath + '/', eventToCreate);
      return applicationResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async update(eventId: number, eventToUpdate: EventUpdateDTO): Promise<ApiResponse<undefined>> {
    try {
      const applicationResponse = await apiClient.patch<undefined>(`${this.basePath}/${eventId}`, eventToUpdate);
      return applicationResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getAll(): Promise<ApiResponse<EventModel[]>> {
    try {
      const applicationResponse = await apiClient.get<EventReadDTO[]>(this.basePath);
      return applicationResponse.processPayload((payload) => {
        return payload.map(EventModel.fromDTO);
      });
    } catch (error) {
      console.log(error);

      return ApiResponse.UnknownErrorResponse();
    }
  }

  async get(
    query: EventFilterOptionsDTO,
    pagination: PagePaginationQueryDtoParams,
  ): Promise<ApiResponse<PagePaginationResultDto<EventModel>>> {
    try {
      const applicationResponse = await apiClient.get<PagePaginationResultDto<EventReadDTO>>(this.basePath, { ...query, ...pagination });
      return applicationResponse.processPagedPayload((payload) => {
        return payload.map(EventModel.fromDTO);
      });
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getById(id: number): Promise<ApiResponse<EventModel>> {
    try {
      const applicationResponse = await apiClient.get<EventReadDTO>(`${this.basePath}/${id}`);
      return applicationResponse.processPayload(EventModel.fromDTO);
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getCount({ labelValueIds }: { labelValueIds?: number[] } = {}): Promise<ApiResponse<number>> {
    try {
      const applicationResponse = await apiClient.get<{ count: number }>(`${this.basePath}/count`, { labelValueIds });
      return applicationResponse.processPayload((payload) => {
        return payload.count;
      });
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getCountsByType(): Promise<ApiResponse<EventCountOverTimeByTypeDTO[]>> {
    try {
      const applicationResponse = await apiClient.get<EventCountOverTimeByTypeDTO[]>(`${this.basePath}/type/count`);
      return applicationResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getEventGroups(): Promise<ApiResponse<EventGroupListDto[]>> {
    try {
      const eventGroupResponse = await apiClient.get<EventGroupListDto[]>(`${this.basePath}/group`);
      return eventGroupResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getEventGroupByUuid(eventGroupUuid: string): Promise<ApiResponse<EventGroupDto>> {
    try {
      const eventGroupResponse = await apiClient.get<EventGroupDto>(`${this.basePath}/group/${eventGroupUuid}`);
      return eventGroupResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getEventCountGroupedByTime(groupBy: GroupPerTimeType): Promise<ApiResponse<EventCountOverTimeDTO[]>> {
    try {
      const eventGroupResponse = await apiClient.get<EventCountOverTimeDTO[]>(`${this.basePath}/counts`, { groupBy: groupBy });
      return eventGroupResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }
}

export const eventService = new EventService();
